// apps/provider/src/services/api/problemList.js
// Problem list (patient-level diagnosis management) methods
//
// Methods: getPatientProblemList, getPatientDiagnosis, createPatientDiagnosis,
//          updatePatientDiagnosis, deletePatientDiagnosis, batchCreatePatientDiagnoses,
//          searchSnomedCodes, getSnomedCrosswalk
//
// Response types: @prism-ehr/shared → PatientDiagnosisResponse, SnomedSearchResult

/**
 * @returns {Object} Problem list domain methods (bound to `this` of the composed apiService)
 */
export function createProblemListMethods() {
  return {
    /**
     * Get patient problem list (diagnoses).
     *
     * Xano: GET /patient-diagnoses
     * Xano file: patient-diagnoses_GET.xs
     *
     * @param {string} patientId
     * @param {Object} [filters]
     * @param {string} [filters.clinical_status] - Filter by status (active, resolved, etc.)
     * @param {number} [filters.page]
     * @param {number} [filters.per_page]
     * @returns {Promise<import('@prism-ehr/shared').PatientDiagnosisResponse[]>}
     */
    async getPatientProblemList(patientId, filters = {}) {
      const params = new URLSearchParams({ patient_id: patientId });
      if (filters.clinical_status) params.set('clinical_status', filters.clinical_status);
      if (filters.page) params.set('page', filters.page);
      if (filters.per_page) params.set('per_page', filters.per_page);
      return this.request(`/patient-diagnoses?${params.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single patient diagnosis.
     *
     * Xano: GET /patient-diagnoses/{id}
     * Xano file: patient-diagnoses_patient-diagnoses-id_GET.xs
     *
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared').PatientDiagnosisResponse>}
     */
    async getPatientDiagnosis(id) {
      return this.request(`/patient-diagnoses/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Create a patient diagnosis (add to problem list).
     *
     * Xano: POST /patient-diagnoses
     * Xano file: patient-diagnoses_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').PatientDiagnosisResponse>}
     */
    async createPatientDiagnosis(data) {
      return this.request('/patient-diagnoses', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a patient diagnosis.
     *
     * Xano: PATCH /patient-diagnoses/{id}
     * Xano file: patient-diagnoses_patient-diagnoses-id_PATCH.xs
     *
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').PatientDiagnosisResponse>}
     */
    async updatePatientDiagnosis(id, data) {
      return this.request(`/patient-diagnoses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a patient diagnosis.
     *
     * Xano: DELETE /patient-diagnoses/{id}
     * Xano file: patient-diagnoses_patient-diagnoses-id_DELETE.xs
     *
     * @param {number} id
     * @returns {Promise<void>}
     */
    async deletePatientDiagnosis(id) {
      return this.request(`/patient-diagnoses/${id}`, {
        method: 'DELETE'
      });
    },

    /**
     * Batch create patient diagnoses (for case seeding and encounter prompt).
     *
     * Xano: POST /patient-diagnoses-batch
     * Xano file: patient-diagnoses-batch_POST.xs
     *
     * @param {Object[]} items
     * @returns {Promise<import('@prism-ehr/shared').PatientDiagnosisResponse[]>}
     */
    async batchCreatePatientDiagnoses(items) {
      return this.request('/patient-diagnoses-batch', {
        method: 'POST',
        timeoutMs: 60000,
        body: JSON.stringify({ items })
      });
    },

    /**
     * Search SNOMED CT codes (CORE Problem List Subset).
     *
     * Xano: GET /search/snomed-codes
     * Xano file: search_snomed-codes_GET.xs
     *
     * @param {string} query
     * @returns {Promise<import('@prism-ehr/shared').SnomedSearchResult[]>}
     */
    async searchSnomedCodes(query) {
      return this.request(`/search/snomed-codes?query=${encodeURIComponent(query)}`, {
        method: 'GET'
      });
    },

    /**
     * Get SNOMED-to-ICD-10 crosswalk mappings for a concept.
     *
     * Xano: GET /snomed-codes-crosswalk
     * Xano file: snomed-codes-crosswalk_GET.xs
     *
     * @param {string} conceptId
     * @returns {Promise<{code: string, description: string, map_priority: number}[]>}
     */
    async getSnomedCrosswalk(conceptId) {
      return this.request(`/snomed-codes-crosswalk?snomed_concept_id=${encodeURIComponent(conceptId)}`, {
        method: 'GET'
      });
    },
  };
}
